import { useEffect, useState } from 'react';
import {
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  Upload,
  Button,
  message,
  Space,
  Row,
  Col,
  Typography
} from 'antd';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { UploadOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';
import dayjs from 'dayjs';

import { Alert, AlertType, AlertStatus } from '@/types/community/alert';
import { addAlert, updateAlert } from '@/api/alerts';
import { useAuth } from '@/contexts/AuthContext';
import { BlockApartmentUnitSelector } from '@/components/common/BlockApartmentUnitSelector';
import { AmenitySelector } from '@/components/common/AmenitySelector';
import { AssetSelector } from '@/components/common/AssetSelector';
import type { FileMetadata } from '@/types/common';

const { TextArea } = Input;
const { Title } = Typography;

interface AlertModalProps {
  open: boolean;
  alert?: Alert | null;
  onSuccess: () => void;
  onCancel: () => void;
}

const ALERT_TYPES: { value: AlertType; label: string }[] = [
  { value: 'emergency', label: 'Emergency' },
  { value: 'maintenance', label: 'Maintenance' },
  { value: 'security', label: 'Security' },
  { value: 'weather', label: 'Weather' },
  { value: 'other', label: 'Other' }
];

const ALERT_STATUSES: { value: AlertStatus; label: string }[] = [
  { value: 'active', label: 'Active' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'cancelled', label: 'Cancelled' }
];

export default function AlertModal({ open, alert, onCancel }: AlertModalProps) {
  const { t } = useTranslation(['alerts', 'common']);
  const { firestoreUser } = useAuth();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [blockData, setBlockData] = useState<{ id: string; name: string } | null>(null);
  const [apartmentUnitData, setApartmentUnitData] = useState<{ id: string; name: string } | null>(null);
  const [amenityData, setAmenityData] = useState<{ id: string; name: string } | null>(null);
  const [assetData, setAssetData] = useState<{ id: string; name: string } | null>(null);

  // Mutations
  const addAlertMutation = useMutation({
    mutationFn: (data: Omit<Alert, 'id'>) => addAlert(data, firestoreUser!),
    onSuccess: () => {
      message.success(t('alerts:addSuccess'));
      queryClient.invalidateQueries({ queryKey: ['alerts'] });
      handleCancel();
    },
    onError: (error: any) => {
      console.error('Add alert error:', error);
      const errorMessage = error?.message || error?.toString() || t('common:errorOccurred');
      message.error(`Failed to add alert: ${errorMessage}`);
    }
  });

  const updateAlertMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Omit<Alert, 'id'>> }) => 
      updateAlert(id, data, firestoreUser!),
    onSuccess: () => {
      message.success(t('alerts:updateSuccess'));
      queryClient.invalidateQueries({ queryKey: ['alerts'] });
      handleCancel();
    },
    onError: (error: any) => {
      console.error('Update alert error:', error);
      const errorMessage = error?.message || error?.toString() || t('common:errorOccurred');
      message.error(`Failed to update alert: ${errorMessage}`);
    }
  });

  // Initialize form when alert changes
  useEffect(() => {
    if (open) {
      if (alert) {
        form.setFieldsValue({
          title: alert.title,
          description: alert.description,
          type: alert.type,
          status: alert.status,
          startTime: alert.startTime ? dayjs(alert.startTime) : undefined,
          endTime: alert.endTime ? dayjs(alert.endTime) : undefined,
          blockId: alert.block?.id,
          apartmentUnitId: alert.apartmentUnit?.id,
          amenityId: alert.amenity?.id,
          assetId: alert.asset?.id
        });
        setBlockData(alert.block || null);
        setApartmentUnitData(alert.apartmentUnit || null);
        setAmenityData(alert.amenity || null);
        setAssetData(alert.asset || null);
        if (alert.attachments) {
          const files: UploadFile[] = alert.attachments.map((attachment, index) => ({
            uid: `-${index}`,
            name: attachment.name,
            status: 'done',
            url: attachment.url
          }));
          setFileList(files);
        }
      } else {
        form.resetFields();
        setFileList([]);
        setBlockData(null);
        setApartmentUnitData(null);
        setAmenityData(null);
        setAssetData(null);
      }
    }
  }, [alert, form, open]);

  const handleSubmit = async (values: any) => {
    if (!firestoreUser) {
      message.error(t('common:notAuthenticated'));
      return;
    }

    // Additional client-side validation
    if (!values.title?.trim()) {
      message.error('Alert title is required');
      return;
    }
    if (!values.description?.trim()) {
      message.error('Alert description is required');
      return;
    }
    if (!values.type) {
      message.error('Alert type is required');
      return;
    }
    if (!values.status) {
      message.error('Alert status is required');
      return;
    }
    if (!values.startTime) {
      message.error('Alert start time is required');
      return;
    }

    try {
      const attachments: FileMetadata[] = fileList
        .filter(file => file.status === 'done')
        .map(file => ({
          id: file.uid || `file-${Date.now()}-${Math.random()}`,
          name: file.name || '',
          url: file.url || '',
          size: file.size || 0,
          type: file.type || '',
          path: file.url || '',
          uploadedAt: new Date(),
          mimeType: file.type || ''
        }));
      const alertData: Omit<Alert, 'id'> = {
        title: values.title.trim(),
        description: values.description.trim(),
        type: values.type,
        status: values.status,
        startTime: values.startTime?.toDate() || new Date(),
        endTime: values.endTime?.toDate() || undefined,
        attachments: attachments.length > 0 ? attachments : undefined,
        block: blockData || undefined,
        apartmentUnit: apartmentUnitData || undefined,
        amenity: amenityData || undefined,
        asset: assetData || undefined,
        workspace: {
          id: firestoreUser.workspace!.id,
          name: firestoreUser.workspace!.name,
          slug: firestoreUser.workspace!.slug
        }
      };

      if (alert) {
        updateAlertMutation.mutate({ id: alert.id, data: alertData });
      } else {
        addAlertMutation.mutate(alertData);
      }
    } catch (error: any) {
      console.error('Form submission error:', error);
      message.error(`Form submission failed: ${error?.message || 'Unknown error'}`);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setFileList([]);
    setBlockData(null);
    setApartmentUnitData(null);
    setAmenityData(null);
    setAssetData(null);
    onCancel();
  };

  const handleFileChange = ({ fileList: newFileList }: { fileList: UploadFile[] }) => {
    setFileList(newFileList);
  };

  const uploadProps = {
    beforeUpload: () => false,
    fileList,
    onChange: handleFileChange,
    multiple: true
  };

  return (
    <Modal
      title={
        <Title level={4} style={{ margin: 0 }}>
          {alert ? t('alerts:editAlert') : t('alerts:addAlert')}
        </Title>
      }
      open={open}
      onCancel={handleCancel}
      footer={null}
      width={800}
      destroyOnHidden
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          status: 'active',
          type: 'emergency',
          startTime: dayjs()
        }}
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="title"
              label={t('alerts:title')}
              rules={[{ required: true, message: t('common:fieldRequired') }]}
            >
              <Input placeholder={t('alerts:titlePlaceholder')} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="type"
              label={t('alerts:type')}
              rules={[{ required: true, message: t('common:fieldRequired') }]}
            >
              <Select placeholder={t('alerts:selectType')}>
                {ALERT_TYPES.map(type => (
                  <Select.Option key={type.value} value={type.value}>
                    {type.label}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>
        <Form.Item
          name="description"
          label={t('alerts:description')}
          rules={[{ required: true, message: t('common:fieldRequired') }]}
        >
          <TextArea rows={4} placeholder={t('alerts:descriptionPlaceholder')} />
        </Form.Item>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="status"
              label={t('alerts:status')}
              rules={[{ required: true, message: t('common:fieldRequired') }]}
            >
              <Select placeholder={t('alerts:selectStatus')}>
                {ALERT_STATUSES.map(status => (
                  <Select.Option key={status.value} value={status.value}>
                    {status.label}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="startTime"
              label={t('alerts:startTime')}
              rules={[{ required: true, message: t('common:fieldRequired') }]}
            >
              <DatePicker showTime style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="endTime"
              label={t('alerts:endTime')}
            >
              <DatePicker showTime style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>
        <BlockApartmentUnitSelector
          onBlockDataChange={setBlockData}
          onApartmentUnitDataChange={setApartmentUnitData}
        />
        <Form.Item
          name="amenityId"
          label={t('alerts:amenity')}
        >
          <AmenitySelector onAmenityDataChange={setAmenityData} />
        </Form.Item>
        <Form.Item
          name="assetId"
          label={t('alerts:asset')}
        >
          <AssetSelector onAssetDataChange={setAssetData} />
        </Form.Item>
        <Form.Item
          label={t('alerts:attachments')}
        >
          <Upload {...uploadProps}>
            <Button icon={<UploadOutlined />}>{t('common:actions.upload')}</Button>
          </Upload>
        </Form.Item>
        <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
          <Space>
            <Button onClick={handleCancel}>{t('common:actions.cancel')}</Button>
            <Button type="primary" htmlType="submit" loading={addAlertMutation.isPending || updateAlertMutation.isPending}>
              {alert ? t('common:actions.update') : t('common:actions.add')}
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
} 